import clsx from 'clsx';
import React, { ReactNode, useRef, useState } from 'react';
import CloseIcon from '../../assets/icons/sidebar-cross-icon.svg?react';

interface TagsInputProps {
  otherClasses?: {
    container?: string;
    input?: string;
    label?: string;
  };
  disabled?: boolean;
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  name?: string;
  value?: string[];
  onChange?: (tags: string[]) => void;
  placeholder?: string;
  errorMessage?: string;
  label?: string | ReactNode;
}
const Pill = ({
  label,
  onRemove,
  error,
}: {
  label: string;
  onRemove: (label: string) => void;
  error?: boolean;
}) => (
  <div
    className={clsx(
      'bg-neutral-tones-sidebar font-inter-variable text-form-label text-neutral-tones-text flex items-center gap-2 rounded-full border-2 px-2 py-1 font-normal',
      error ? 'border-brand-posh-pink' : 'border-transparent',
    )}
  >
    {label}
    <button
      type="button"
      onClick={() => onRemove(label)}
      className="bg-brand-byzantine flex h-4 min-h-4 w-4 min-w-4 cursor-pointer items-center justify-center rounded-full"
    >
      <CloseIcon className="[&>g>path]:fill-neutral-tones-background h-2 w-2" />
    </button>
  </div>
);

const TagsInput: React.FC<TagsInputProps> = ({
  otherClasses,
  disabled,
  handleBlur,
  name,
  value,
  onChange,
  placeholder,
  errorMessage,
  label,
}) => {
  const tagsInputClasses = clsx(
    otherClasses?.container,
    'flex flex-col gap-2.5 items-start relative w-full',
  );
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      e.stopPropagation();
      const updatedTags = [...(value || []), inputValue.trim()];
      onChange?.(updatedTags as any);
      setInputValue('');
    }
  };

  const handleRemoveTag = (index: number) => {
    const updatedTags = value?.filter((_, i) => i !== index);
    onChange?.(updatedTags as any);
  };

  const labelClasses = clsx(
    otherClasses?.label,
    'text-form-label  font-bold -tracking-form-label font-inter-variable',
    disabled
      ? 'text-neutral-tones-text/35 cursor-not-allowed'
      : 'text-neutral-tones-text',
  );

  return (
    <div className={tagsInputClasses} data-testid="tags-input">
      {label && (
        <label htmlFor={name} className={labelClasses}>
          {label}
        </label>
      )}
      <div
        onClick={() => {
          inputRef.current?.focus();
        }}
        className={clsx(
          'rounded-10 relative flex w-full flex-wrap items-center gap-2 px-5 py-15 ring',
          errorMessage
            ? 'ring-brand-posh-pink ring-2'
            : isFocused
              ? 'ring-brand-byzantine ring-2'
              : 'ring-neutral-tones-text/50',
        )}
      >
        {value?.map((tag: string, index) => {
          const isDuplicate = value.filter((t) => t === tag).length > 1;
          return (
            <Pill
              key={index}
              label={tag}
              onRemove={() => handleRemoveTag(index)}
              error={isDuplicate}
            />
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          onBlur={(e) => {
            setIsFocused(false);
            handleBlur?.(e);
          }}
          onFocus={() => setIsFocused(true)}
          name={name}
          id={name}
          className={clsx(
            'border-l-neutral-tones-text text-neutral-tones-text w-fit flex-1 border-l bg-transparent outline-none focus:border-l-transparent',
          )}
          placeholder={placeholder}
        />
        {errorMessage && (
          <span
            role="alert"
            className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal"
          >
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  );
};

export default TagsInput;
